import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, PlayCircle, FileText, PencilRuler, HelpCircle, CheckCircle2, Download, ExternalLink, Lock, Award } from "lucide-react";
import { toast } from "sonner";

const ICONS = { video: PlayCircle, document: FileText, practice: PencilRuler, quiz: HelpCircle };
const API = process.env.REACT_APP_BACKEND_URL;

function QuizRunner({ courseId, lesson, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setAns = (qid, val) => setAnswers((s) => ({ ...s, [qid]: val }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/my-courses/${courseId}/lessons/${lesson.id}/quiz-submit`, { answers });
      setResult(r.data);
      if (r.data.passed) { toast.success(`¡Aprobado con ${r.data.score_pct}%! 🎉`); onPassed(); }
      else toast.error(`Obtuviste ${r.data.score_pct}% — necesitás ${r.data.pass_pct}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando el quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const detailFor = (qid) => result?.detail?.find((d) => d.question_id === qid);

  return (
    <div className="space-y-4" data-testid="quiz-runner">
      <div className="text-xs text-slate-500">Nota mínima: {lesson.quiz_pass_pct ?? 70}% {lesson.quiz_max_attempts ? `· ${lesson.quiz_max_attempts} intentos máx.` : ""}</div>
      {(lesson.quiz_questions || []).map((q, qi) => {
        const d = detailFor(q.id);
        return (
          <div key={q.id} className={`p-4 rounded-lg border ${d ? (d.correct ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50") : "border-slate-200"}`} data-testid={`player-q-${qi}`}>
            <div className="text-sm font-medium text-slate-800 mb-2">P{qi + 1}. {q.prompt} {d && (d.correct ? "✅" : "❌")}</div>
            {q.type === "single" && q.options.map((o) => (
              <label key={o.id} className="flex items-center gap-2 py-1 text-sm text-slate-700 cursor-pointer">
                <input type="radio" name={q.id} checked={(answers[q.id] || [])[0] === o.id} onChange={() => setAns(q.id, [o.id])} data-testid={`ans-${qi}-${o.id}`} />
                {o.text}
              </label>
            ))}
            {q.type === "multiple" && q.options.map((o) => (
              <label key={o.id} className="flex items-center gap-2 py-1 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={(answers[q.id] || []).includes(o.id)} onCheckedChange={(v) => {
                  const cur = answers[q.id] || [];
                  setAns(q.id, v ? [...cur, o.id] : cur.filter((x) => x !== o.id));
                }} data-testid={`ans-${qi}-${o.id}`} />
                {o.text}
              </label>
            ))}
            {q.type === "fill_blank" && (
              <div className="space-y-2">
                {Array.from({ length: q.blank_count }).map((_, bi) => (
                  <Input key={bi} placeholder={`Espacio ${bi + 1}`} className="h-8 max-w-xs" value={(answers[q.id] || [])[bi] || ""} onChange={(e) => {
                    const cur = [...(answers[q.id] || Array(q.blank_count).fill(""))];
                    cur[bi] = e.target.value;
                    setAns(q.id, cur);
                  }} data-testid={`blank-${qi}-${bi}`} />
                ))}
              </div>
            )}
          </div>
        );
      })}
      <Button className="btn-brand" onClick={submit} disabled={submitting} data-testid="quiz-submit-btn">
        {submitting ? "Calificando…" : "Enviar respuestas"}
      </Button>
      {result && (
        <div className={`text-sm font-medium ${result.passed ? "text-emerald-700" : "text-red-600"}`} data-testid="quiz-result">
          Resultado: {result.score_pct}% {result.passed ? "— ¡Aprobado!" : `— Necesitás ${result.pass_pct}%. ${result.max_attempts ? `Intentos: ${result.attempts_used}/${result.max_attempts}` : "Podés intentarlo de nuevo."}`}
        </div>
      )}
    </div>
  );
}

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [current, setCurrent] = useState(null);
  const [certLoading, setCertLoading] = useState(false);

  const downloadCertificate = async () => {
    setCertLoading(true);
    try {
      const r = await api.get(`/my-courses/${courseId}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado-${data.course.title.replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("¡Certificado descargado! 🎓");
    } catch (e) {
      toast.error("No se pudo generar el certificado");
    } finally {
      setCertLoading(false);
    }
  };

  const load = useCallback(async (keepCurrent) => {
    try {
      const r = await api.get(`/my-courses/${courseId}`);
      setData(r.data);
      const all = r.data.modules.flatMap((m) => m.lessons);
      if (keepCurrent && current) {
        setCurrent(all.find((l) => l.id === current.id) || all[0]);
      } else {
        setCurrent(all.find((l) => !l.completed && !l.locked) || all.find((l) => !l.locked) || all[0]);
      }
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);
  useEffect(() => { load(false); }, [load]);

  const markComplete = async () => {
    try {
      await api.post(`/my-courses/${courseId}/lessons/${current.id}/complete`);
      toast.success("Lección completada ✓");
      load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (denied) return <div className="text-sm text-slate-500">No tenés acceso a este curso.</div>;
  if (!data || !current) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="course-player-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/my-courses")} data-testid="back-to-my-courses"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-900">{data.course.title}</div>
          <div className="flex items-center gap-2 mt-1">
            <Progress value={data.course.progress_pct} className="h-2 w-40" />
            <span className="text-xs text-slate-500" data-testid="player-progress">{data.course.progress_pct}% · {data.course.completed_count}/{data.course.lesson_count}</span>
          </div>
        </div>
        {data.course.progress_pct === 100 && data.course.certificate_enabled && (
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={downloadCertificate} disabled={certLoading} data-testid="certificate-btn">
            <Award className="h-4 w-4 mr-1" /> {certLoading ? "Generando…" : "Descargar certificado"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Lesson content */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="lesson-content">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="font-semibold text-slate-900">{current.title}</h2>
              {current.completed && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Completada ✓</Badge>}
            </div>

            {current.type === "video" && current.youtube_id && (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${current.youtube_id}?rel=0&modestbranding=1`} title={current.title} allowFullScreen allow="autoplay; encrypted-media" />
              </div>
            )}
            {current.type === "document" && (
              <div className="space-y-3">
                {current.doc_view_url && <iframe src={`${API}${current.doc_view_url}`} title={current.title} className="w-full h-[65vh] rounded-lg border border-slate-200" />}
                {current.doc_url && <a href={current.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline">Abrir documento <ExternalLink className="h-3.5 w-3.5" /></a>}
              </div>
            )}
            {current.type === "practice" && (
              <div className="space-y-3">
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">{current.body}</div>
                {current.attachment_url && <a href={`${API}${current.attachment_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline"><Download className="h-3.5 w-3.5" /> Descargar material</a>}
              </div>
            )}
            {current.type === "quiz" && (
              <QuizRunner courseId={courseId} lesson={current} onPassed={() => load(true)} />
            )}

            {current.type !== "quiz" && !current.completed && (
              <Button className="btn-brand mt-4" onClick={markComplete} data-testid="mark-complete-btn">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Marcar como completada
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar curriculum */}
        <div className="space-y-3" data-testid="player-sidebar">
          {data.modules.map((m, mi) => (
            <div key={m.id} className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${m.locked ? "opacity-70" : ""}`}>
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-sm font-medium text-slate-800 flex items-center justify-between">
                <span>Módulo {mi + 1} — {m.title}</span>
                {m.locked && <Lock className="h-3.5 w-3.5 text-slate-400" data-testid={`module-locked-${m.id}`} />}
              </div>
              <div className="divide-y divide-slate-50">
                {m.lessons.map((l) => {
                  const Icon = ICONS[l.type] || FileText;
                  const active = current.id === l.id;
                  return (
                    <button key={l.id} type="button"
                      onClick={() => l.locked ? toast.error("Aprobá los quizzes del módulo anterior para desbloquear este módulo 🔒") : setCurrent(l)}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm ${l.locked ? "text-slate-400 cursor-not-allowed" : active ? "bg-brand-blue/5 text-brand-blue font-medium" : "text-slate-700 hover:bg-slate-50"}`}
                      data-testid={`player-lesson-${l.id}`}>
                      {l.locked ? <Lock className="h-4 w-4 text-slate-300 shrink-0" /> : l.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Icon className="h-4 w-4 text-slate-400 shrink-0" />}
                      <span className="flex-1 truncate">{l.title}</span>
                      {l.duration_minutes ? <span className="text-[10px] text-slate-400">{l.duration_minutes}m</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
